import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import type { KycStatus } from "@/modules/customers/domain/types";
import {
  ORDER_STATUS_LABELS_LAO,
  type OrderStatus,
} from "@/modules/orders/domain/types";
import {
  formatDateLocal,
  formatDateTimeLocal,
  parseCalendarDateFromInput,
} from "@/shared/lib/date-time";
import { formatPhoneDisplay } from "@/shared/lib/phone";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { QueryState } from "@/shared/ui/QueryState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { cn } from "@/shared/lib/utils";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import type { LucideIcon } from "lucide-react";
import {
  ArrowLeftIcon,
  Building2Icon,
  CrownIcon,
  GiftIcon,
  LockIcon,
  PhoneIcon,
  ShoppingBagIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UserRoundIcon,
  WalletIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import {
  useCustomerDetailQuery,
  useUpdateCustomerKycMutation,
} from "../api/queries";
import { KycDocumentPreview } from "../ui/KycDocumentPreview";
import {
  CustomerMembershipEmpty,
  CustomerMembershipSection,
} from "../ui/CustomerMembershipSection";

const ORDER_STATUS_BADGE: Record<
  string,
  "default" | "secondary" | "destructive" | "outline"
> = {
  DRAFT: "outline",
  CONFIRMED: "secondary",
  PREPARING: "default",
  READY: "default",
  COMPLETED: "secondary",
  CANCELLED: "destructive",
};

const CHANNEL_LABELS = {
  APP: "ແອັບ",
  WALKIN: "ເຂົ້າຮ້ານ",
} as const;

const WALLET_STATUS_LABELS: Record<string, string> = {
  ACTIVE: "ໃຊ້ງານ",
  SUSPENDED: "ລະງັບ",
  CLOSED: "ປິດແລ້ວ",
};

const KYC_STATUS_LABELS: Record<KycStatus, string> = {
  NONE: "ຍັງບໍ່ຍື່ນ",
  PENDING: "ລໍຖ້າກວດ",
  VERIFIED: "ຢັ້ງຢືນແລ້ວ",
  REJECTED: "ບໍ່ຜ່ານ",
};

const KYC_STATUS_BADGE: Record<
  KycStatus,
  "default" | "secondary" | "destructive" | "outline"
> = {
  NONE: "outline",
  PENDING: "secondary",
  VERIFIED: "default",
  REJECTED: "destructive",
};

const DETAIL_ACCENTS = {
  stone: {
    card: "border-stone-950/15 bg-gradient-to-br from-card via-card to-stone-950/[0.03] dark:border-stone-500/12 dark:to-stone-950/15",
    header:
      "border-stone-950/10 bg-gradient-to-r from-stone-950/[0.06] to-transparent dark:border-stone-500/10 dark:from-stone-900/50",
    radial:
      "radial-gradient(ellipse 120% 80% at 100% 0%, rgb(120 113 108 / 0.11), transparent 55%)",
    iconBox:
      "bg-stone-500/10 ring-stone-600/25 ring-inset dark:bg-stone-400/10 dark:ring-stone-400/15",
    icon: "text-stone-800 dark:text-stone-400",
  },
  amber: {
    card: "border-amber-950/15 bg-gradient-to-br from-card via-card to-amber-950/[0.04] dark:border-amber-500/15 dark:to-amber-950/20",
    header:
      "border-amber-950/10 bg-gradient-to-r from-amber-950/[0.07] to-transparent dark:border-amber-500/10 dark:from-amber-950/40",
    radial:
      "radial-gradient(ellipse 120% 80% at 100% 0%, rgb(166 124 82 / 0.12), transparent 55%)",
    iconBox:
      "bg-amber-500/10 ring-amber-600/25 ring-inset dark:bg-amber-400/10 dark:ring-amber-400/20",
    icon: "text-amber-800 dark:text-amber-400",
  },
  emerald: {
    card: "border-emerald-950/15 bg-gradient-to-br from-card via-card to-emerald-950/[0.035] dark:border-emerald-900/18 dark:to-emerald-950/18",
    header:
      "border-emerald-950/10 bg-gradient-to-r from-emerald-950/[0.07] to-transparent dark:border-emerald-800/15 dark:from-emerald-950/35",
    radial:
      "radial-gradient(ellipse 120% 80% at 100% 0%, rgb(5 150 105 / 0.1), transparent 55%)",
    iconBox:
      "bg-emerald-500/10 ring-emerald-600/25 ring-inset dark:bg-emerald-400/10 dark:ring-emerald-400/20",
    icon: "text-emerald-800 dark:text-emerald-400",
  },
  neutral: {
    card: "border-border/40 bg-gradient-to-br from-card via-card to-muted/30",
    header:
      "border-border/30 bg-gradient-to-r from-muted/40 to-transparent",
    radial:
      "radial-gradient(ellipse 120% 80% at 100% 0%, rgb(115 115 115 / 0.09), transparent 55%)",
    iconBox:
      "bg-neutral-500/10 ring-neutral-600/25 ring-inset dark:bg-neutral-400/10 dark:ring-neutral-400/15",
    icon: "text-neutral-800 dark:text-neutral-400",
  },
} as const;

function DetailSectionCard({
  accent,
  label,
  title,
  subtitle,
  icon: Icon,
  children,
  contentClassName,
}: {
  accent: keyof typeof DETAIL_ACCENTS;
  label: string;
  title: string;
  subtitle?: string;
  icon: LucideIcon;
  children: ReactNode;
  contentClassName?: string;
}) {
  const a = DETAIL_ACCENTS[accent];
  return (
    <Card
      className={cn("relative gap-0 overflow-hidden py-0 shadow-none", a.card)}
    >
      <div className={cn("relative border-b px-5 py-5", a.header)}>
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.35] dark:opacity-25"
          style={{ background: a.radial }}
        />
        <div className="relative flex items-center gap-3">
          <div
            className={cn(
              "flex size-12 shrink-0 items-center justify-center rounded-2xl ring-1",
              a.iconBox,
            )}
          >
            <Icon className={cn("size-6", a.icon)} strokeWidth={1.75} />
          </div>
          <div className="min-w-0 space-y-0.5">
            <p className="text-[0.65rem] text-muted-foreground uppercase tracking-[0.12em]">
              {label}
            </p>
            <CardTitle className="font-semibold text-lg leading-tight tracking-tight">
              {title}
            </CardTitle>
            {subtitle ? (
              <p className="text-muted-foreground text-xs leading-snug">
                {subtitle}
              </p>
            ) : null}
          </div>
        </div>
      </div>
      <CardContent className={cn("p-5 pt-5", contentClassName)}>
        {children}
      </CardContent>
    </Card>
  );
}

function CustomerKycSection({
  customerId,
  kycStatus,
  kycVerifiedAt,
  kycDocumentRef,
  canUpdate,
}: {
  customerId: string;
  kycStatus: KycStatus;
  kycVerifiedAt: string | null;
  kycDocumentRef: string | null;
  canUpdate: boolean;
}) {
  const updateKyc = useUpdateCustomerKycMutation(customerId);

  const save = (status: KycStatus) => {
    updateKyc.mutate({ kycStatus: status });
  };

  return (
    <DetailSectionCard
      accent="stone"
      label="KYC"
      title="ການຢັ້ງຢືນຕົວຕົນ"
      subtitle="ກວດເອກະສານຈາກລູກຄ້າ ແລະ ອັບເດດສະຖານະ"
      icon={ShieldCheckIcon}
    >
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border/60 bg-muted/20 px-4 py-3">
          <span className="text-muted-foreground text-xs tracking-wide">
            ສະຖານະ
          </span>
          <Badge variant={KYC_STATUS_BADGE[kycStatus]}>
            {KYC_STATUS_LABELS[kycStatus]}
          </Badge>
        </div>

        {kycVerifiedAt ? (
          <div className="flex items-center justify-between gap-3 rounded-2xl border border-border/60 bg-muted/20 px-4 py-3">
            <span className="text-muted-foreground text-xs tracking-wide">
              ຢັ້ງຢືນເມື່ອ
            </span>
            <span className="text-end text-foreground text-sm tabular-nums">
              {formatDateTimeLocal(kycVerifiedAt)}
            </span>
          </div>
        ) : null}

        <div className="space-y-2">
          <span className="text-muted-foreground text-xs tracking-wide">
            ເອກະສານຈາກລູກຄ້າ
          </span>
          <KycDocumentPreview documentRef={kycDocumentRef} />
        </div>

        {canUpdate ? (
          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              size="sm"
              disabled={updateKyc.isPending}
              onClick={() => save("VERIFIED")}
            >
              ຢັ້ງຢືນ (Verify)
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={updateKyc.isPending}
              onClick={() => save("PENDING")}
            >
              ລໍຖ້າກວດ
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={updateKyc.isPending}
              onClick={() => save("REJECTED")}
            >
              ປະຕິເສດ
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={updateKyc.isPending}
              onClick={() => save("NONE")}
            >
              ຣີເຊັດ
            </Button>
          </div>
        ) : (
          <p className="text-muted-foreground text-xs">
            ຕ້ອງມີສິດ{" "}
            <span className="font-mono">customers:update</span> ເພື່ອແກ້ KYC
          </p>
        )}
      </div>
    </DetailSectionCard>
  );
}

export function CustomerDetailPage() {
  const nav = useNavigate({ from: "/app/customers/$id" });
  const { id } = useParams({ from: "/app/customers/$id" });
  const { data, ...queryResult } = useCustomerDetailQuery(id ?? "");
  const { has } = usePermissions();
  const canUpdateKyc = has("customers:update");

  const displayName = data?.customer.name?.trim() || data?.customer.phone;

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ລູກຄ້າ", to: "/app/customers" },
            { label: "ລາຍລະອຽດ" },
          ]}
        />
      </Header>
      <Main>
        <QueryState
          result={queryResult}
          title="ກຳລັງໂຫຼດລູກຄ້າ"
          description="ກຳລັງດຶງຂໍ້ມູນ"
          variant="fullscreen"
        >
          {!data ? null : (
            <div className="space-y-6">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h1 className="font-bold text-2xl tracking-tight">
                    {displayName}
                  </h1>
                  <p className="flex items-center gap-2 text-muted-foreground text-sm">
                    <PhoneIcon className="size-4 shrink-0" />
                    {formatPhoneDisplay(data.customer.phone)}
                  </p>
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  className="mb-2 -ml-2"
                  onClick={() =>
                    nav({
                      to: "/app/customers",
                      search: {
                        q: "",
                        limit: 20,
                        offset: 0,
                        sortField: "createdAt",
                        sortOrder: "desc",
                      },
                    })
                  }
                >
                  <ArrowLeftIcon className="mr-1 size-4" />
                  ກັບລາຍການ
                </Button>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <CustomerKycSection
                  customerId={data.customer.id}
                  kycStatus={data.customer.kycStatus}
                  kycVerifiedAt={data.customer.kycVerifiedAt}
                  kycDocumentRef={data.customer.kycDocumentRef}
                  canUpdate={canUpdateKyc}
                />

                <DetailSectionCard
                  accent="stone"
                  label="Profile"
                  title="ຂໍ້ມູນທົ່ວໄປ"
                  subtitle="ຂໍ້ມູນລະບົບ ແລະ ສາຂາທີ່ລູກຄ້າເລືອກເປັນຄ່າເລີ່ມຕົ້ນ"
                  icon={UserRoundIcon}
                >
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3 rounded-2xl border border-border/60 bg-muted/20 px-4 py-3">
                      <span className="text-muted-foreground text-xs tracking-wide">
                        ຊື່
                      </span>
                      <span className="max-w-[65%] text-end font-medium text-foreground text-sm">
                        {data.customer.name ?? "–"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-3 rounded-2xl border border-border/60 bg-muted/20 px-4 py-3">
                      <span className="text-muted-foreground text-xs tracking-wide">
                        ວັນເກີດ
                      </span>
                      <span className="text-end text-foreground text-sm tabular-nums">
                        {data.customer.dateOfBirth
                          ? formatDateLocal(
                              parseCalendarDateFromInput(
                                data.customer.dateOfBirth,
                              ),
                            )
                          : "–"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-3 rounded-2xl border border-border/60 bg-muted/20 px-4 py-3">
                      <span className="text-muted-foreground text-xs tracking-wide">
                        ສາຂາເລີ່ມຕົ້ນ
                      </span>
                      <span className="flex max-w-[65%] items-center justify-end gap-1.5 text-end font-medium text-foreground text-sm">
                        <Building2Icon
                          className="size-3.5 shrink-0 text-muted-foreground"
                          strokeWidth={1.75}
                        />
                        {data.customer.defaultBranchName ?? "–"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-3 rounded-2xl border border-border/60 bg-muted/20 px-4 py-3">
                      <span className="text-muted-foreground text-xs tracking-wide">
                        ສ້າງເມື່ອ
                      </span>
                      <span className="text-end text-foreground text-sm tabular-nums">
                        {formatDateTimeLocal(data.customer.createdAt)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-3 rounded-2xl border border-border/60 bg-muted/20 px-4 py-3">
                      <span className="text-muted-foreground text-xs tracking-wide">
                        ອັບເດດຫຼ້າສຸດ
                      </span>
                      <span className="text-end text-foreground text-sm tabular-nums">
                        {formatDateTimeLocal(data.customer.updatedAt)}
                      </span>
                    </div>
                  </div>
                </DetailSectionCard>

                <DetailSectionCard
                  accent="amber"
                  label="Loyalty"
                  title="ລາຍແຕ້ມ"
                  subtitle="ຍອດແຕ້ມສະສົມຂອງລູກຄ້າ"
                  icon={GiftIcon}
                >
                  {data.loyalty ? (
                    <div className="rounded-2xl border border-border/70 bg-muted/25 p-4 transition-colors hover:bg-muted/40">
                      <div className="mb-3 flex items-center gap-2">
                        <span className="flex size-8 items-center justify-center rounded-xl bg-amber-500/15 text-amber-800 dark:text-amber-400">
                          <SparklesIcon
                            className="size-4"
                            strokeWidth={1.75}
                          />
                        </span>
                        <span className="text-muted-foreground text-xs tracking-wide">
                          ແຕ້ມຄົງເຫຼືອ
                        </span>
                      </div>
                      <p className="font-bold text-3xl text-foreground tabular-nums tracking-tight">
                        {data.loyalty.pointsBalance.toLocaleString("lo-LA")}
                      </p>
                      <p className="mt-1 text-[0.7rem] text-muted-foreground leading-none tracking-wide">
                        ຄະແນນສະສົມໃນລະບົບ
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-muted-foreground/25 border-dashed bg-muted/15 px-6 py-10 text-center">
                      <div className="flex size-14 items-center justify-center rounded-2xl bg-muted/60 ring-1 ring-border/60">
                        <GiftIcon
                          className="size-7 text-muted-foreground"
                          strokeWidth={1.5}
                        />
                      </div>
                      <div className="space-y-1">
                        <p className="font-medium text-foreground text-sm">
                          ຍັງບໍ່ມີບັນຊີລາຍແຕ້ມ
                        </p>
                        <p className="max-w-[240px] text-muted-foreground text-xs leading-relaxed">
                          ລູກຄ້າທ່ານນີ້ຍັງບໍ່ເຄີຍເຂົ້າລະບົບເກັບແຕ້ມ ຫຼື
                          ຍັງບໍ່ຖືກລົງທະບຽນໃນໂປຣແກຣມສະມາຊິກ
                        </p>
                      </div>
                    </div>
                  )}
                </DetailSectionCard>

                <DetailSectionCard
                  accent="neutral"
                  label="Membership"
                  title="ບັດສະມາຊິກ"
                  subtitle="ລະດັບບັດ, ສິດທິພິເສດ ແລະ ຄວາມຄືບໜ້າ upgrade"
                  icon={CrownIcon}
                >
                  {data.membership ? (
                    <CustomerMembershipSection
                      membership={data.membership}
                      visits={data.membershipVisits}
                    />
                  ) : (
                    <CustomerMembershipEmpty />
                  )}
                </DetailSectionCard>
              </div>

              <DetailSectionCard
                accent="emerald"
                label="Wallet"
                title="ກະເປົາເງິນ"
                subtitle="ຍອດຄົງເຫຼືອ ແລະ ສະຖານະບັນຊີ (ຕ້ອງມີ wallet:read ເພື່ອເບິ່ງ)"
                icon={WalletIcon}
              >
                {!data.wallet.accessible ? (
                  <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-muted-foreground/25 border-dashed bg-muted/15 px-6 py-10 text-center">
                    <div className="flex size-14 items-center justify-center rounded-2xl bg-muted/50 ring-1 ring-border/60">
                      <LockIcon
                        className="size-7 text-muted-foreground"
                        strokeWidth={1.5}
                      />
                    </div>
                    <div className="space-y-1">
                      <p className="font-medium text-foreground text-sm">
                        ບໍ່ສາມາດເບິ່ງຂໍ້ມູນ Wallet
                      </p>
                      <p className="max-w-[280px] text-muted-foreground text-xs leading-relaxed">
                        ບັນຊີຂອງທ່ານຕ້ອງມີສິດ{" "}
                        <span className="font-mono text-[0.7rem]">
                          wallet:read
                        </span>{" "}
                        ເພື່ອເບິ່ງຍອດ ແລະ ລາຍການບັນຊີ
                      </p>
                    </div>
                  </div>
                ) : data.wallet.accounts.length === 0 ? (
                  <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-muted-foreground/25 border-dashed bg-muted/15 px-6 py-10 text-center">
                    <div className="flex size-14 items-center justify-center rounded-2xl bg-muted/50 ring-1 ring-border/60">
                      <WalletIcon
                        className="size-7 text-muted-foreground"
                        strokeWidth={1.5}
                      />
                    </div>
                    <p className="font-medium text-foreground text-sm">
                      ບໍ່ມີບັນຊີ Wallet
                    </p>
                    <p className="max-w-[260px] text-muted-foreground text-xs leading-relaxed">
                      ລູກຄ້າຍັງບໍ່ມີກະເປົາເງິນທີ່ເຊື່ອມກັບລະບົບ
                    </p>
                  </div>
                ) : (
                  <ul className="space-y-3">
                    {data.wallet.accounts.map((a) => (
                      <li
                        key={a.id}
                        className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-border/70 bg-muted/20 p-4 transition-colors hover:bg-muted/35"
                      >
                        <div className="min-w-0 space-y-1">
                          <Badge variant="secondary" className="font-normal">
                            {WALLET_STATUS_LABELS[a.status] ?? a.status}
                          </Badge>
                          <p className="truncate font-mono text-[0.65rem] text-muted-foreground">
                            {a.id}
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-3">
                          <span className="font-bold text-foreground text-xl tabular-nums tracking-tight">
                            {a.balanceLak.toLocaleString("lo-LA")}{" "}
                            <span className="font-medium text-muted-foreground text-sm">
                              LAK
                            </span>
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-xl"
                            onClick={() =>
                              nav({
                                to: "/app/wallet/limits/$walletId",
                                params: { walletId: a.id },
                              })
                            }
                          >
                            ວົງເງິນ
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </DetailSectionCard>

              <DetailSectionCard
                accent="neutral"
                label="Orders"
                title="ອໍເດີຫຼ້າສຸດ"
                subtitle="ສູງສຸດ 50 ລາຍການ ຈາກທຸກສາຂາ — ກົດເລກອໍເດີເພື່ອເບິ່ງລາຍລະອຽດ"
                icon={ShoppingBagIcon}
                contentClassName={
                  data.orders.length === 0 ? undefined : "p-0 pt-0"
                }
              >
                {data.orders.length === 0 ? (
                  <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-muted-foreground/25 border-dashed bg-muted/15 px-6 py-10 text-center">
                    <div className="flex size-14 items-center justify-center rounded-2xl bg-muted/50 ring-1 ring-border/60">
                      <ShoppingBagIcon
                        className="size-7 text-muted-foreground"
                        strokeWidth={1.5}
                      />
                    </div>
                    <p className="font-medium text-foreground text-sm">
                      ຍັງບໍ່ມີອໍເດີ
                    </p>
                    <p className="max-w-[260px] text-muted-foreground text-xs leading-relaxed">
                      ອໍເດີທີ່ຜູກນຳລູກຄ້າທ່ານນີ້ຈະສະແດງຢູ່ບ່ອນນີ້
                    </p>
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-b-2xl border-border/60 border-t">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-border/80 border-b bg-muted/40 text-left text-[0.65rem] text-muted-foreground uppercase tracking-[0.08em]">
                            <th className="px-4 py-3 font-medium">ເລກອໍເດີ</th>
                            <th className="px-4 py-3 font-medium">ສາຂາ</th>
                            <th className="px-4 py-3 font-medium">ຊ່ອງທາງ</th>
                            <th className="px-4 py-3 font-medium">ສະຖານະ</th>
                            <th className="px-4 py-3 text-end font-medium">
                              ຍອດ
                            </th>
                            <th className="px-4 py-3 font-medium">ວັນທີ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.orders.map((o) => (
                            <tr
                              key={o.id}
                              className="border-border/50 border-b transition-colors last:border-0 hover:bg-muted/25"
                            >
                              <td className="px-4 py-3">
                                <Link
                                  to="/app/orders/$id"
                                  params={{ id: o.id }}
                                  className="font-medium text-primary hover:underline"
                                >
                                  {o.orderNo}
                                </Link>
                              </td>
                              <td className="px-4 py-3 text-muted-foreground">
                                {o.branchName}
                              </td>
                              <td className="px-4 py-3">
                                {CHANNEL_LABELS[o.channel]}
                              </td>
                              <td className="px-4 py-3">
                                <Badge
                                  variant={
                                    ORDER_STATUS_BADGE[o.status] ?? "secondary"
                                  }
                                >
                                  {ORDER_STATUS_LABELS_LAO[
                                    o.status as OrderStatus
                                  ] ?? o.status}
                                </Badge>
                              </td>
                              <td className="px-4 py-3 text-end font-medium tabular-nums">
                                {o.total.toLocaleString("lo-LA")} {o.currency}
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-muted-foreground tabular-nums">
                                {formatDateTimeLocal(o.createdAt)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </DetailSectionCard>
            </div>
          )}
        </QueryState>
      </Main>
    </>
  );
}
